import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Trash } from '@phosphor-icons/react';

export default function ShapePropertiesPanel({ measurement, shape, shapeType, onUpdate, onReplaceShape, onDelete, onClose }) {
  const [name, setName] = useState(measurement?.label || '');
  const [desc, setDesc] = useState(measurement?.description || '');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [rotation, setRotation] = useState('0');
  const [radius, setRadius] = useState('');
  const [diameter, setDiameter] = useState('');
  const [markerAddress, setMarkerAddress] = useState('');

  useEffect(() => {
    setName(measurement?.label || '');
    setDesc(measurement?.description || '');
    setRotation(measurement?.rotation?.toString() || '0');
    // Recompute length/width whenever the underlying shape changes (initial mount,
    // shape change, AND on undo/redo/resize-driven coordinate updates).
    if (shapeType === 'rectangle') {
      const { l, w } = getLengthWidth();
      if (l > 0 && w > 0) {
        setLength(l.toFixed(2));
        setWidth(w.toFixed(2));
      }
    }
    if (shapeType === 'circle' && shape?.getRadius) {
      const r = shape.getRadius();
      setRadius(r.toFixed(2));
      setDiameter((r * 2).toFixed(2));
    }
    // Reverse geocode for markers
    const isM = shapeType === 'marker' || shapeType === 'text_label';
    if (isM) {
      setMarkerAddress('');
      const pos = shape?.getPosition?.();
      if (pos && window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: pos }, (results, status) => {
          if (status === 'OK' && results?.[0]) {
            setMarkerAddress(results[0].formatted_address);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurement?.id, measurement?.rotation, measurement?.coordinates, measurement?.value, measurement?.radius]);

  function G() { return window.google.maps.geometry.spherical; }

  function getCenter() {
    if (!shape) return null;
    if (shape.getBounds) {
      const b = shape.getBounds();
      if (b) return b.getCenter();
    }
    if (shape.getPath) {
      const path = shape.getPath();
      let lat = 0, lng = 0;
      for (let i = 0; i < path.getLength(); i++) {
        lat += path.getAt(i).lat();
        lng += path.getAt(i).lng();
      }
      return new window.google.maps.LatLng(lat / path.getLength(), lng / path.getLength());
    }
    return null;
  }

  function getLengthWidth() {
    if (!shape) return { l: 0, w: 0 };
    if (shapeType === 'rectangle' && shape.getBounds) {
      const b = shape.getBounds();
      if (!b) return { l: 0, w: 0 };
      const ne = b.getNorthEast(), sw = b.getSouthWest();
      const l = G().computeDistanceBetween(
        new window.google.maps.LatLng(sw.lat(), sw.lng()),
        new window.google.maps.LatLng(sw.lat(), ne.lng())
      );
      const w = G().computeDistanceBetween(
        new window.google.maps.LatLng(sw.lat(), sw.lng()),
        new window.google.maps.LatLng(ne.lat(), sw.lng())
      );
      return { l, w };
    }
    if (shapeType === 'rectangle' && shape.getPath) {
      const path = shape.getPath();
      if (path.getLength() < 4) return { l: 0, w: 0 };
      const p0 = path.getAt(0), p1 = path.getAt(1), p2 = path.getAt(2);
      return {
        l: G().computeDistanceBetween(p0, p1),
        w: G().computeDistanceBetween(p1, p2)
      };
    }
    return { l: 0, w: 0 };
  }

  function computeDimensions() {
    const { l, w } = getLengthWidth();
    setLength(l.toFixed(2));
    setWidth(w.toFixed(2));
  }

  function getArea() {
    if (!shape) return 0;
    if (shape.getBounds && shapeType === 'rectangle') {
      const { l, w } = getLengthWidth();
      return l * w;
    }
    if (shape.getPath) {
      return window.google.maps.geometry.spherical.computeArea(shape.getPath());
    }
    if (shapeType === 'circle') return Math.PI * (shape.getRadius() || 0) ** 2;
    return measurement?.value || 0;
  }

  function getPerimeter() {
    if (!shape) return 0;
    if (shape.getBounds && shapeType === 'rectangle') {
      const { l, w } = getLengthWidth();
      return 2 * (l + w);
    }
    if (shape.getPath) {
      return window.google.maps.geometry.spherical.computeLength(shape.getPath());
    }
    if (shapeType === 'circle') return 2 * Math.PI * (shape.getRadius() || 0);
    return 0;
  }

  function handleNameBlur() {
    if (name !== measurement?.label) onUpdate({ label: name });
  }

  function handleDescBlur() {
    if (desc !== measurement?.description) onUpdate({ description: desc });
  }

  function computeRotatedCorners(center, halfL, halfW, angleDeg) {
    const rad = (-angleDeg * Math.PI) / 180; // Negative for clockwise rotation
    const cosA = Math.cos(rad), sinA = Math.sin(rad);
    // Length = East-West (horizontal), Width = North-South (vertical)
    const corners = [
      { dx: -halfL, dy: halfW },   // TL
      { dx: halfL, dy: halfW },    // TR
      { dx: halfL, dy: -halfW },   // BR
      { dx: -halfL, dy: -halfW },  // BL
    ];
    return corners.map(({ dx, dy }) => {
      const rx = dx * cosA - dy * sinA;
      const ry = dx * sinA + dy * cosA;
      // Move from center: rx meters east, ry meters north
      const east = G().computeOffset(center, Math.abs(rx), rx >= 0 ? 90 : 270);
      const point = G().computeOffset(
        new window.google.maps.LatLng(center.lat(), east.lng()),
        Math.abs(ry), ry >= 0 ? 0 : 180
      );
      return point;
    });
  }

  function applyRotation(newRotation) {
    if (!shape || !onReplaceShape) return;
    const r = parseFloat(newRotation);
    if (isNaN(r)) return;
    // Skip no-op blurs (input refocus, tab-through with no edit) so we don't pollute
    // the undo stack or needlessly convert a Rectangle into a Polygon.
    const prevR = parseFloat(measurement?.rotation) || 0;
    if (Math.abs(r - prevR) < 1e-6) return;
    const center = getCenter();
    if (!center) return;
    if (shapeType === 'rectangle') {
      // Use the user's current length/width inputs, not shape edges
      const l = parseFloat(length) || 0;
      const w = parseFloat(width) || 0;
      if (l <= 0 || w <= 0) return;
      const corners = computeRotatedCorners(center, l / 2, w / 2, r);
      onReplaceShape(measurement.id, corners, { rotation: r });
    } else if (shapeType === 'polygon' && shape.getPath) {
      const path = shape.getPath();
      if (path.getLength() < 3) return;
      const prevRot = parseFloat(measurement?.rotation) || 0;
      const delta = r - prevRot;
      const rotated = rotateVertices(center, path, delta);
      onReplaceShape(measurement.id, rotated, { rotation: r });
    }
  }

  function rotateVertices(center, path, angleDeg) {
    const rad = (-angleDeg * Math.PI) / 180;
    const cosA = Math.cos(rad), sinA = Math.sin(rad);
    const points = [];
    for (let i = 0; i < path.getLength(); i++) {
      const p = path.getAt(i);
      // Convert to meters offset from center
      const dxM = G().computeDistanceBetween(center, new window.google.maps.LatLng(center.lat(), p.lng())) * (p.lng() >= center.lng() ? 1 : -1);
      const dyM = G().computeDistanceBetween(center, new window.google.maps.LatLng(p.lat(), center.lng())) * (p.lat() >= center.lat() ? 1 : -1);
      // Rotate
      const rx = dxM * cosA - dyM * sinA;
      const ry = dxM * sinA + dyM * cosA;
      // Convert back to lat/lng
      const east = G().computeOffset(center, Math.abs(rx), rx >= 0 ? 90 : 270);
      const point = G().computeOffset(
        new window.google.maps.LatLng(center.lat(), east.lng()),
        Math.abs(ry), ry >= 0 ? 0 : 180
      );
      points.push(point);
    }
    return points;
  }

  function applyDimensions(newLength, newWidth) {
    if (!shape || !onReplaceShape) return;
    const l = parseFloat(newLength);
    const w = parseFloat(newWidth);
    if (isNaN(l) || isNaN(w) || l <= 0 || w <= 0) return;
    // Skip no-op blurs.
    const cur = getLengthWidth();
    if (Math.abs(cur.l - l) < 0.01 && Math.abs(cur.w - w) < 0.01) return;
    const center = getCenter();
    if (!center) return;
    const r = parseFloat(rotation) || 0;
    const corners = computeRotatedCorners(center, l / 2, w / 2, r);
    onReplaceShape(measurement.id, corners, { rotation: r });
  }

  function applyRadius(newRadius) {
    if (!shape || shapeType !== 'circle') return;
    const r = parseFloat(newRadius);
    if (isNaN(r) || r <= 0) return;
    shape.setRadius(r);
    const newArea = Math.PI * r * r;
    const fmtD = (v) => v >= 1000 ? `${(v/1000).toFixed(2)} km` : `${v.toFixed(2)} m`;
    onUpdate({ radius: r, value: newArea, details: `Type: roof_area\nArea: ${newArea.toFixed(2)} m\u00B2\nRadius: ${fmtD(r)}` });
  }

  function applyLineRotation(newRotation) {
    if (!shape || !shape.getPath) return;
    const r = parseFloat(newRotation);
    if (isNaN(r)) return;
    const prevRot = parseFloat(measurement?.rotation) || 0;
    const delta = r - prevRot;
    if (delta === 0) return;
    const path = shape.getPath();
    if (path.getLength() < 2) return;
    // Get center of line
    let cLat = 0, cLng = 0;
    for (let i = 0; i < path.getLength(); i++) { cLat += path.getAt(i).lat(); cLng += path.getAt(i).lng(); }
    const center = new window.google.maps.LatLng(cLat / path.getLength(), cLng / path.getLength());
    // Rotate all points around center
    const rad = (-delta * Math.PI) / 180;
    const cosA = Math.cos(rad), sinA = Math.sin(rad);
    const newPoints = [];
    for (let i = 0; i < path.getLength(); i++) {
      const p = path.getAt(i);
      const dxM = G().computeDistanceBetween(center, new window.google.maps.LatLng(center.lat(), p.lng())) * (p.lng() >= center.lng() ? 1 : -1);
      const dyM = G().computeDistanceBetween(center, new window.google.maps.LatLng(p.lat(), center.lng())) * (p.lat() >= center.lat() ? 1 : -1);
      const rx = dxM * cosA - dyM * sinA;
      const ry = dxM * sinA + dyM * cosA;
      const east = G().computeOffset(center, Math.abs(rx), rx >= 0 ? 90 : 270);
      const point = G().computeOffset(new window.google.maps.LatLng(center.lat(), east.lng()), Math.abs(ry), ry >= 0 ? 0 : 180);
      newPoints.push(point);
    }
    // Update the path in place
    const newPath = shape.getPath();
    for (let i = 0; i < newPoints.length; i++) {
      newPath.setAt(i, newPoints[i]);
    }
    // Save coordinates and rotation
    const coords = newPoints.map(p => [p.lng(), p.lat()]);
    onUpdate({ rotation: r, coordinates: coords });
  }

  const area = getArea();
  const perimeter = getPerimeter();
  const isRect = shapeType === 'rectangle';
  const isCircle = shapeType === 'circle';
  const isPoly = shapeType === 'polygon';
  const isLine = shapeType === 'polyline' || shapeType === 'line' || shapeType === 'arrow' || shapeType === 'multi_distance' || shapeType === 'height';
  const isMarker = shapeType === 'marker' || shapeType === 'text_label' || (shapeType && shapeType.startsWith('site_'));
  const isSite = !!(shapeType && shapeType.startsWith('site_'));
  const showRotation = isRect || isPoly;

  function getLineLength() {
    if (!shape || !shape.getPath) return 0;
    return G().computeLength(shape.getPath());
  }

  function getLineBearing() {
    if (!shape || !shape.getPath) return 0;
    const path = shape.getPath();
    if (path.getLength() < 2) return 0;
    const p1 = path.getAt(0);
    const p2 = path.getAt(path.getLength() - 1);
    return G().computeHeading(p1, p2);
  }

  const lineLength = isLine ? getLineLength() : 0;
  const lineBearing = isLine ? getLineBearing() : 0;

  function getMarkerCoords() {
    if (!shape) return { lat: 0, lng: 0 };
    const pos = shape.getPosition ? shape.getPosition() : null;
    if (pos) return { lat: pos.lat(), lng: pos.lng() };
    if (measurement?.coordinates?.length) return { lat: measurement.coordinates[0][1], lng: measurement.coordinates[0][0] };
    return { lat: 0, lng: 0 };
  }

  function toDMS(deg, isLat) {
    const abs = Math.abs(deg);
    const d = Math.floor(abs);
    const mFull = (abs - d) * 60;
    const m = Math.floor(mFull);
    const s = Math.floor((mFull - m) * 60);
    const dir = isLat ? (deg >= 0 ? 'N' : 'S') : (deg >= 0 ? 'E' : 'W');
    return `${d}\u00B0 ${m}' ${s}" ${dir}`;
  }

  const markerCoords = isMarker ? getMarkerCoords() : null;

  return (
    <div
      className="absolute right-3 top-14 z-30 w-[280px] bg-card border border-border rounded-xl shadow-2xl animate-fadeIn"
      data-testid="shape-properties-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 pb-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          className="text-sm font-bold bg-transparent border-0 outline-none w-full p-0 focus:ring-0"
          placeholder="Shape name"
          data-testid="prop-name-input"
        />
        <button onClick={onClose} className="ml-2 shrink-0 w-6 h-6 flex items-center justify-center rounded-md hover:bg-secondary/80 text-muted-foreground" data-testid="prop-close-btn">
          <X size={14} weight="bold" />
        </button>
      </div>
      <div className="px-3 pb-2">
        <input
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={handleDescBlur}
          className="text-xs text-muted-foreground bg-transparent border-0 outline-none w-full p-0 focus:ring-0"
          placeholder="+ Add description"
          data-testid="prop-desc-input"
        />
      </div>

      <div className="border-t border-border mx-3" />

      {/* Marker Info */}
      {isMarker && markerCoords && (
        <div className="px-3 py-3 space-y-2">
          {markerAddress && (
            <CopyRow text={markerAddress} testId="prop-marker-address" />
          )}
          <CopyRow text={`${markerCoords.lat.toFixed(6)}, ${markerCoords.lng.toFixed(6)}`} testId="prop-marker-latlng" mono />
          <CopyRow text={`${toDMS(markerCoords.lat, true)}, ${toDMS(markerCoords.lng, false)}`} testId="prop-marker-dms" mono />
        </div>
      )}

      {/* Measurements for shapes */}
      {!isMarker && !isLine && (
        <div className="px-3 py-3 space-y-2.5">
          <p className="text-xs font-bold text-foreground">Measurements</p>

          <Row label="Area" value={area.toFixed(2)} unit="m²" readOnly testId="prop-area" />
          <Row label="Perimeter" value={perimeter.toFixed(2)} unit="m" readOnly testId="prop-perimeter" />

          {isRect && (
            <>
              <Row label="Length" value={length} unit="m" testId="prop-length-input"
                onChange={(v) => setLength(v)}
                onBlur={() => applyDimensions(length, width)} />
              <Row label="Width" value={width} unit="m" testId="prop-width-input"
                onChange={(v) => setWidth(v)}
                onBlur={() => applyDimensions(length, width)} />
            </>
          )}

          {isCircle && (
            <>
              <Row label="Radius" value={radius} unit="m" testId="prop-radius-input"
                onChange={(v) => { setRadius(v); setDiameter((parseFloat(v) * 2 || 0).toFixed(2)); }}
                onBlur={() => applyRadius(radius)} />
              <Row label="Diameter" value={diameter} unit="m" testId="prop-diameter-input"
                onChange={(v) => { setDiameter(v); setRadius((parseFloat(v) / 2 || 0).toFixed(2)); }}
                onBlur={() => applyRadius((parseFloat(diameter) / 2).toFixed(2))} />
            </>
          )}
        </div>
      )}

      {/* Rotation — sits in its own section below Measurements (separator
          above) so the slider has room for a comfortable label + value box
          without competing with the readonly Length/Width column widths. */}
      {!isMarker && !isLine && showRotation && (
        <div className="px-3 py-3 space-y-2.5 border-t border-border">
          <p className="text-xs font-bold text-foreground">Rotation</p>
          <RotationRow value={rotation} onChange={setRotation} onCommit={applyRotation} testId="prop-rotation-input" />
        </div>
      )}

      {/* Line Info */}
      {isLine && (
        <div className="px-3 py-3 space-y-2.5">
          <p className="text-xs font-bold text-foreground">Measurements</p>
          {shapeType === 'height' ? (
            <>
              <Row label="Height" value={(measurement?.value || 0).toFixed(2)} unit="m" readOnly testId="prop-height-value" />
              <Row label="Base Length" value={lineLength.toFixed(2)} unit="m" readOnly testId="prop-line-length" />
            </>
          ) : (
            <>
              <Row label="Length" value={lineLength.toFixed(2)} unit="m" readOnly testId="prop-line-length" />
              <Row label="Bearing" value={((lineBearing + 360) % 360).toFixed(1)} unit="deg" readOnly testId="prop-line-bearing" />
            </>
          )}
        </div>
      )}

      {/* Rotation — own section below the line measurements for the same
          reason as the shape branch above. */}
      {isLine && (
        <div className="px-3 py-3 space-y-2.5 border-t border-border">
          <p className="text-xs font-bold text-foreground">Rotation</p>
          <RotationRow value={rotation} onChange={setRotation} onCommit={applyLineRotation} testId="prop-line-rotation-input" />
        </div>
      )}

      {/* Delete */}
      <div className="px-3 pb-3">
        <button
          onClick={onDelete}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-md transition-colors"
          data-testid="prop-delete-btn"
        >
          <Trash size={14} weight="bold" /> Delete Shape
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, unit, readOnly, testId, onChange, onBlur }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        {readOnly ? (
          <span className="text-xs font-medium bg-secondary/60 px-2.5 py-1 rounded-md min-w-[85px] text-right" data-testid={testId}>{value}</span>
        ) : (
          <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
            className="text-xs font-medium bg-card border border-border px-2.5 py-1 rounded-md min-w-[85px] w-[85px] text-right outline-none focus:border-primary"
            data-testid={testId}
          />
        )}
        <span className="text-xs text-muted-foreground w-8">{unit}</span>
      </div>
    </div>
  );
}


/**
 * Rotation editor. Sits inside its own panel section so it has the full panel
 * width to work with — a 0–360° slider takes most of the row, and a small
 * numeric input (with `deg` suffix) sits on the right for precise typing.
 *
 * UX rules:
 *  - Slider drives the rotation LIVE — every change commits to the shape so
 *    the user sees it spin under their finger.
 *  - Numeric input commits on blur or Enter. Typing `-15` or `425` is
 *    normalised to `345` / `65` so values stay in [0, 360).
 *  - Both stay in sync — drag the slider, the number updates; type a value,
 *    the slider thumb jumps to it.
 */
function RotationRow({ value, onChange, onCommit, testId }) {
  const numeric = (() => {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return 0;
    return ((n % 360) + 360) % 360;
  })();

  const commit = (v) => {
    const n = parseFloat(v);
    const safe = Number.isFinite(n) ? ((n % 360) + 360) % 360 : 0;
    onCommit?.(safe);
  };

  return (
    <div className="flex items-center gap-2.5">
      <input
        type="range"
        min={0}
        max={360}
        step={1}
        value={numeric}
        // LIVE-commit on every change so the shape rotates as the user drags.
        onChange={(e) => {
          onChange?.(e.target.value);
          commit(e.target.value);
        }}
        aria-label="Rotation in degrees"
        className="flex-1 min-w-0 h-1.5 appearance-none rounded-full bg-secondary cursor-pointer accent-primary"
        data-testid={`${testId}-slider`}
      />
      <input
        type="number"
        min={0}
        max={360}
        step={1}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={() => commit(value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        className="shrink-0 text-xs font-medium bg-card border border-border px-2.5 py-1 rounded-md w-[60px] text-right outline-none focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        data-testid={testId}
      />
      <span className="shrink-0 text-xs text-muted-foreground w-7">deg</span>
    </div>
  );
}


function CopyRow({ text, testId, mono }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="flex items-start justify-between gap-2 group" data-testid={testId}>
      <p className={`text-xs leading-relaxed ${mono ? 'font-mono text-muted-foreground' : 'text-foreground'}`}>{text}</p>
      <button
        onClick={handleCopy}
        className="shrink-0 mt-0.5 w-6 h-6 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-secondary/80 text-muted-foreground transition-opacity"
        title="Copy"
        data-testid={`${testId}-copy`}
      >
        {copied ? <Check size={14} weight="bold" className="text-emerald-500" /> : <Copy size={14} />}
      </button>
    </div>
  );
}
